import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, Play, Square, RefreshCw, Settings, Trash2, FileCode2, Database,
  Server, Clock, ListChecks, Power,
} from "lucide-react";

const RECOVERY = ["FULL", "SIMPLE", "BULK_LOGGED"];
const PAGE_VERIFY = ["CHECKSUM", "TORN_PAGE_DETECTION", "NONE"];
const COMPAT = ["160 (SQL Server 2022)", "150 (SQL Server 2019)", "140 (SQL Server 2017)", "130 (SQL Server 2016)"];
const AUTH_MODES = ["Windows Authentication mode", "SQL Server and Windows Authentication mode"];

// ---------------------------------------------------------------- context menu
const MENUS = {
  server: [
    { id: "newquery", label: "New Query", icon: FileCode2 },
    { id: "refresh", label: "Refresh", icon: RefreshCw },
    { id: "sep" },
    { id: "properties", label: "Properties", icon: Settings },
  ],
  database: [
    { id: "newquery", label: "New Query", icon: FileCode2 },
    { id: "script", label: "Script Database as CREATE", icon: FileText },
    { id: "refresh", label: "Refresh", icon: RefreshCw },
    { id: "sep" },
    { id: "properties", label: "Properties", icon: Settings },
  ],
  job: [
    { id: "run", label: "Start Job at Step…", icon: Play },
    { id: "enable", label: "Enable", icon: Power },
    { id: "disable", label: "Disable", icon: Power },
    { id: "history", label: "View History", icon: Clock },
    { id: "refresh", label: "Refresh", icon: RefreshCw },
    { id: "sep" },
    { id: "properties", label: "Properties", icon: Settings },
  ],
  agent: [
    { id: "agent-start", label: "Start", icon: Play },
    { id: "agent-stop", label: "Stop", icon: Square },
    { id: "refresh", label: "Refresh", icon: RefreshCw },
  ],
};

export function ObjectContextMenu({ menu, onClose, onAction }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const h = () => onClose();
    window.addEventListener("click", h);
    window.addEventListener("contextmenu", h);
    return () => { window.removeEventListener("click", h); window.removeEventListener("contextmenu", h); };
  }, [menu, onClose]);
  if (!menu) return null;
  const items = MENUS[menu.type] || [];
  return createPortal(
    <div ref={ref} data-testid="sql-context-menu"
      className="fixed z-[100] min-w-[220px] rounded-md border border-border bg-popover text-popover-foreground shadow-xl py-1 text-sm"
      style={{ top: Math.min(menu.y, window.innerHeight - 300), left: Math.min(menu.x, window.innerWidth - 240) }}
      onClick={(e) => e.stopPropagation()}>
      <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border mb-1 truncate">{menu.label || menu.name}</div>
      {items.map((it, i) => it.id === "sep"
        ? <div key={i} className="h-px bg-border my-1" />
        : <button key={it.id} data-testid={`ctx-${it.id}`}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-accent text-left"
            onClick={() => { onAction(it.id, menu); onClose(); }}>
            {it.icon && <it.icon className="h-3.5 w-3.5 text-muted-foreground" />}{it.label}
          </button>)}
    </div>, document.body);
}

// ---------------------------------------------------------------- prop dialog shell
function PropShell({ open, onClose, icon: Icon, title, pages, active, setActive, onOk, onApply, footerNote, testid }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid={testid}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{title} — administration properties</DialogDescription>
        <div className="px-4 py-3 border-b border-border flex items-center gap-2 bg-muted/40">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}<span className="text-sm font-semibold">{title}</span>
        </div>
        <div className="flex min-h-[420px]">
          <div className="w-48 shrink-0 border-r border-border bg-muted/20 py-2">
            <div className="px-3 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Select a page</div>
            {pages.map((p) => (
              <button key={p.id} onClick={() => setActive(p.id)} data-testid={`page-${p.id}`}
                className={`w-full text-left px-3 py-1.5 text-sm ${active === p.id ? "bg-accent text-foreground font-medium" : "text-muted-foreground hover:bg-accent/60"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex-1 p-4 overflow-y-auto max-h-[60vh]">{pages.find((p) => p.id === active)?.content}</div>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center gap-2 justify-end bg-muted/40">
          {footerNote && <span className="mr-auto text-[11px] text-muted-foreground">{footerNote}</span>}
          <Button variant="outline" size="sm" onClick={onClose} data-testid="prop-cancel">Cancel</Button>
          {onApply && <Button variant="outline" size="sm" onClick={onApply} data-testid="prop-apply">Apply</Button>}
          {onOk && <Button size="sm" onClick={onOk} data-testid="prop-ok">OK</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Grid({ rows }) {
  return <div className="rounded border border-border divide-y divide-border">
    {rows.filter(Boolean).map(([k, v], i) => (
      <div key={i} className="grid grid-cols-2 gap-2 px-3 py-1.5 text-xs">
        <div className="text-muted-foreground">{k}</div><div className="font-medium break-all">{v}</div>
      </div>))}
  </div>;
}
function Row2({ label, children }) {
  return <div className="grid grid-cols-[220px_1fr] items-center gap-3 py-1.5 text-xs"><label className="text-muted-foreground">{label}</label><div>{children}</div></div>;
}
function Sel({ value, onChange, options, testid }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-8 rounded border border-border bg-background px-2 text-xs w-full max-w-xs">
    {options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
}
function Chk({ checked, onChange, label, testid }) {
  return <label className="flex items-center gap-2 text-xs py-1"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {label}</label>;
}

// ---------------------------------------------------------------- Server Properties
export function ServerPropertiesDialog({ open, onClose, server, onSave }) {
  const [active, setActive] = useState("general");
  const [d, setD] = useState(server);
  useEffect(() => setD(server), [server]);
  const save = async (close) => {
    try {
      const { data } = await api.post("/sql/admin/server", {
        max_memory_mb: Number(d.max_memory_mb), min_memory_mb: Number(d.min_memory_mb),
        auth_mode: d.auth_mode, remote_connections: d.remote_connections, max_connections: Number(d.max_connections),
      });
      onSave(data); toast.success("Server properties applied."); if (close) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply changes."); }
  };
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const pages = [
    { id: "general", label: "General", content: <Grid rows={[
      ["Name", server.name], ["Product", `Microsoft SQL Server`], ["Version", server.version],
      ["Edition", server.edition], ["Operating System", server.os], ["Platform", "NT x64"],
      ["Server Collation", server.collation], ["Processors", server.processors], ["Memory (MB)", server.memory_mb],
      ["Is Clustered", "False"], ["Is HADR Enabled", "True"],
    ]} /> },
    { id: "memory", label: "Memory", content: <div className="space-y-1 max-w-md">
      <Row2 label="Minimum server memory (MB)"><Input type="number" value={d.min_memory_mb} onChange={(e) => set("min_memory_mb", e.target.value)} className="h-8 max-w-[160px]" data-testid="srv-minmem" /></Row2>
      <Row2 label="Maximum server memory (MB)"><Input type="number" value={d.max_memory_mb} onChange={(e) => set("max_memory_mb", e.target.value)} className="h-8 max-w-[160px]" data-testid="srv-maxmem" /></Row2>
      <p className="text-[11px] text-muted-foreground mt-2">Configure the buffer pool memory limits available to this instance of SQL Server.</p>
    </div> },
    { id: "processors", label: "Processors", content: <Grid rows={[
      ["Number of processors", server.processors], ["Automatically set processor affinity mask for all processors", "True"],
      ["Automatically set I/O affinity mask for all processors", "True"], ["Maximum worker threads", "0 (dynamic)"],
      ["Boost SQL Server priority", "False"],
    ]} /> },
    { id: "security", label: "Security", content: <div className="space-y-2 max-w-lg">
      <div className="text-xs text-muted-foreground">Server authentication</div>
      {AUTH_MODES.map((m) => (
        <label key={m} className="flex items-center gap-2 text-xs"><input type="radio" name="auth" checked={d.auth_mode === m} onChange={() => set("auth_mode", m)} data-testid={`srv-auth-${m.includes("SQL") ? "mixed" : "windows"}`} /> {m}</label>
      ))}
    </div> },
    { id: "connections", label: "Connections", content: <div className="space-y-1 max-w-md">
      <Row2 label="Maximum number of concurrent connections (0 = unlimited)"><Input type="number" value={d.max_connections} onChange={(e) => set("max_connections", e.target.value)} className="h-8 max-w-[120px]" data-testid="srv-maxconn" /></Row2>
      <Chk checked={d.remote_connections} onChange={(v) => set("remote_connections", v)} label="Allow remote connections to this server" testid="srv-remote" />
      <Row2 label="Remote query timeout (seconds)"><span className="text-xs">{server.remote_query_timeout}</span></Row2>
    </div> },
    { id: "dbsettings", label: "Database Settings", content: <Grid rows={[
      ["Default index fill factor", "0"], ["Default backup media retention (days)", "0"],
      ["Data (default)", server.default_data_path], ["Log (default)", server.default_log_path],
      ["Backup (default)", server.default_backup_path],
    ]} /> },
    { id: "advanced", label: "Advanced", content: <Grid rows={[
      ["Max Degree of Parallelism", "0"], ["Cost Threshold for Parallelism", "50"],
      ["Optimize for Ad hoc Workloads", "True"], ["Remote Query Timeout", `${server.remote_query_timeout}s`],
      ["Network Packet Size", "4096"],
    ]} /> },
  ];
  return <PropShell open={open} onClose={onClose} icon={Server} title="Server Properties - HMG-SQL01" testid="server-properties-dialog"
    pages={pages} active={active} setActive={setActive} onOk={() => save(true)} onApply={() => save(false)}
    footerNote="Ready" />;
}

// ---------------------------------------------------------------- Database Properties
export function DatabasePropertiesDialog({ open, onClose, dbProps, onSave }) {
  const [active, setActive] = useState("general");
  const [d, setD] = useState(dbProps);
  useEffect(() => setD(dbProps), [dbProps]);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const save = async (close) => {
    try {
      const { data } = await api.post(`/sql/admin/databases/${dbProps.name}`, {
        recovery_model: d.recovery_model, compatibility_level: d.compatibility_level,
        page_verify: d.page_verify, auto_close: d.auto_close, auto_shrink: d.auto_shrink,
        auto_create_stats: d.auto_create_stats, auto_update_stats: d.auto_update_stats,
        read_only: d.read_only, restrict_access: d.restrict_access,
      });
      onSave(data); toast.success(`Database properties for ${dbProps.name} applied.`); if (close) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply changes."); }
  };
  const saveFile = async (idx, patch) => {
    try {
      const f = d.files[idx];
      const { data } = await api.post(`/sql/admin/databases/${dbProps.name}/files/${f.logical_name}`, patch);
      onSave(data); setD(data); toast.success("File updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update file."); }
  };
  const pages = [
    { id: "general", label: "General", content: <Grid rows={[
      ["Database Name", d.name], ["Owner", d.owner], ["Status", d.state], ["Date Created", new Date(d.created).toLocaleString()],
      ["Size", `${d.size_mb} MB`], ["Space Available", `${d.space_available_mb} MB`], ["Number of Users", "6"],
      ["Collation", d.collation], ["Recovery Model", d.recovery_model], ["Compatibility Level", d.compatibility_level],
    ]} /> },
    { id: "files", label: "Files", content: <div className="space-y-3">
      <Grid rows={[["Database name", d.name], ["Owner", d.owner]]} />
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-muted"><tr>{["Logical Name", "File Type", "Filegroup", "Size (MB)", "Autogrowth / Maxsize", "Path"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{d.files.map((f, i) => (
            <tr key={f.logical_name} className="border-t border-border" data-testid={`db-file-${f.logical_name}`}>
              <td className="px-2 py-1">{f.logical_name}</td><td className="px-2 py-1">{f.type}</td><td className="px-2 py-1">{f.filegroup}</td>
              <td className="px-2 py-1"><Input type="number" defaultValue={f.size_mb} className="h-7 w-20 text-xs" data-testid={`db-file-size-${i}`}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== f.size_mb) saveFile(i, { size_mb: v }); }} /></td>
              <td className="px-2 py-1"><Input defaultValue={f.autogrowth} className="h-7 w-44 text-xs" data-testid={`db-file-growth-${i}`}
                onBlur={(e) => { if (e.target.value !== f.autogrowth) saveFile(i, { autogrowth: e.target.value }); }} /></td>
              <td className="px-2 py-1 font-mono text-muted-foreground">{f.path}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">Increase size or edit autogrowth, then click out of the field to apply. Shrinking below the current size is blocked (use a shrink task).</p>
    </div> },
    { id: "filegroups", label: "Filegroups", content: <Grid rows={[["PRIMARY", "1 file · Default · Read/Write"], ["Files", "Autogrow enabled"]]} /> },
    { id: "options", label: "Options", content: <div className="space-y-2 max-w-xl">
      <Row2 label="Collation"><span className="text-xs">{d.collation}</span></Row2>
      <Row2 label="Recovery model"><Sel value={d.recovery_model} onChange={(v) => set("recovery_model", v)} options={RECOVERY} testid="db-recovery" /></Row2>
      <Row2 label="Compatibility level"><Sel value={d.compatibility_level} onChange={(v) => set("compatibility_level", v)} options={COMPAT} testid="db-compat" /></Row2>
      <Row2 label="Page Verify"><Sel value={d.page_verify} onChange={(v) => set("page_verify", v)} options={PAGE_VERIFY} testid="db-pageverify" /></Row2>
      <Row2 label="Restrict Access"><Sel value={d.restrict_access} onChange={(v) => set("restrict_access", v)} options={["MULTI_USER", "SINGLE_USER", "RESTRICTED_USER"]} testid="db-restrict" /></Row2>
      <div className="pt-2 border-t border-border mt-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Automatic</div>
        <Chk checked={d.auto_close} onChange={(v) => set("auto_close", v)} label="Auto Close" testid="db-autoclose" />
        <Chk checked={d.auto_shrink} onChange={(v) => set("auto_shrink", v)} label="Auto Shrink" testid="db-autoshrink" />
        <Chk checked={d.auto_create_stats} onChange={(v) => set("auto_create_stats", v)} label="Auto Create Statistics" testid="db-autocreatestats" />
        <Chk checked={d.auto_update_stats} onChange={(v) => set("auto_update_stats", v)} label="Auto Update Statistics" testid="db-autoupdatestats" />
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2 mb-1">State</div>
        <Chk checked={d.read_only} onChange={(v) => set("read_only", v)} label="Database Read-Only" testid="db-readonly" />
      </div>
    </div> },
    { id: "permissions", label: "Permissions", content: <Grid rows={[
      ["db_owner", "HMG\\Administrator"], ["db_datareader", "hmg_readonly"], ["db_datawriter", "hmg_app_login"],
      ["Explicit CONNECT", "Granted to public"],
    ]} /> },
  ];
  return <PropShell open={open} onClose={onClose} icon={Database} title={`Database Properties - ${dbProps.name}`} testid="database-properties-dialog"
    pages={pages} active={active} setActive={setActive} onOk={() => save(true)} onApply={() => save(false)} footerNote="Ready" />;
}

// ---------------------------------------------------------------- Job Properties
export function JobPropertiesDialog({ open, onClose, job, initialPage, onChanged }) {
  const [active, setActive] = useState(initialPage || "general");
  const [j, setJ] = useState(job);
  useEffect(() => { setJ(job); if (initialPage) setActive(initialPage); }, [job, initialPage]);
  const doAction = async (action) => {
    try {
      const { data } = await api.post(`/sql/admin/jobs/${encodeURIComponent(j.name)}/action`, { action });
      setJ(data); onChanged(data);
      toast.success(action === "run" ? "Job started successfully." : `Job ${action}d.`);
      if (action === "run") setActive("history");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  const step = j.steps?.[0];
  const pages = [
    { id: "general", label: "General", content: <div className="space-y-3">
      <Grid rows={[["Name", j.name], ["Owner", j.owner], ["Category", j.category], ["Enabled", j.enabled ? "Yes" : "No"], ["Description", j.description]]} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => doAction("run")} data-testid="job-run" className="gap-1.5"><Play className="h-3.5 w-3.5" /> Start Job</Button>
        <Button size="sm" variant="outline" onClick={() => doAction(j.enabled ? "disable" : "enable")} data-testid="job-toggle" className="gap-1.5">
          <Power className="h-3.5 w-3.5" /> {j.enabled ? "Disable" : "Enable"}</Button>
      </div>
    </div> },
    { id: "steps", label: "Steps", content: <div className="space-y-3">
      <div className="rounded border border-border overflow-hidden"><table className="w-full text-[11px]">
        <thead className="bg-muted"><tr>{["Step", "Name", "Type", "On Success", "On Failure"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{(j.steps || []).map((s) => <tr key={s.id} className="border-t border-border"><td className="px-2 py-1">{s.id}</td><td className="px-2 py-1">{s.name}</td><td className="px-2 py-1">{s.type}</td><td className="px-2 py-1">{s.on_success}</td><td className="px-2 py-1">{s.on_fail}</td></tr>)}</tbody>
      </table></div>
      {step && <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Command (Database: {step.database})</div>
        <pre className="bg-[#0b1020] text-emerald-200 text-[11px] p-3 rounded border border-border overflow-auto whitespace-pre-wrap">{step.command}</pre></div>}
    </div> },
    { id: "schedules", label: "Schedules", content: <Grid rows={[
      ["Name", j.schedule?.name], ["Enabled", j.schedule?.enabled ? "Yes" : "No"], ["Frequency", j.schedule?.frequency],
      ["Next Run", j.schedule?.next_run ? new Date(j.schedule.next_run).toLocaleString() : "—"],
    ]} /> },
    { id: "notifications", label: "Notifications", content: <Grid rows={[
      ["E-mail operator", "DBA On-Call (When the job fails)"], ["Write to Windows Application event log", "When the job fails"],
      ["Automatically delete job", "Never"],
    ]} /> },
    { id: "history", label: "History", content: <div className="rounded border border-border overflow-hidden" data-testid="job-history">
      {(j.history || []).length === 0 ? <div className="p-4 text-xs text-muted-foreground">No history yet. Start the job to create a run entry.</div>
        : <table className="w-full text-[11px]"><thead className="bg-muted"><tr>{["Date", "Step", "Status", "Duration", "Message"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{j.history.map((h, i) => <tr key={i} className="border-t border-border"><td className="px-2 py-1 whitespace-nowrap">{new Date(h.date).toLocaleString()}</td><td className="px-2 py-1">{h.step}</td>
            <td className={`px-2 py-1 font-medium ${h.status === "Succeeded" ? "text-emerald-500" : "text-rose-500"}`}>{h.status}</td><td className="px-2 py-1">{h.duration}</td><td className="px-2 py-1 text-muted-foreground">{h.message}</td></tr>)}</tbody></table>}
    </div> },
  ];
  return <PropShell open={open} onClose={onClose} icon={Clock} title={`Job Properties - ${job.name}`} testid="job-properties-dialog"
    pages={pages} active={active} setActive={setActive} onOk={onClose} footerNote={`Agent required to run scheduled jobs`} />;
}
